/**
 * Iter329 · Email Timeline card — Resend events per prospect.
 * Renders a compact timeline of delivered/opened/bounced events by
 * reading the real webhook data. When nothing is available, shows
 * "Sem eventos" honestly.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Mail, Send, Eye, MousePointerClick, AlertTriangle, Clock,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_ICON = {
  sent: Send,
  delivered: Send,
  opened: Eye,
  clicked: MousePointerClick,
  bounced: AlertTriangle,
  complained: AlertTriangle,
  delivery_delayed: Clock,
};

const EVENT_COLOR = {
  sent:              "text-blue-600 border-blue-200 bg-blue-50",
  delivered:         "text-emerald-600 border-emerald-200 bg-emerald-50",
  opened:            "text-indigo-600 border-indigo-200 bg-indigo-50",
  clicked:           "text-violet-600 border-violet-200 bg-violet-50",
  bounced:           "text-red-600 border-red-200 bg-red-50",
  complained:        "text-red-600 border-red-200 bg-red-50",
  delivery_delayed:  "text-amber-600 border-amber-200 bg-amber-50",
};

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function eventKind(raw) {
  return String(raw || "unknown").split(".").pop();
}

export default function ProspectEmailTimelineCard({ prospectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!prospectId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/prospects/${prospectId}/email-timeline`);
        if (alive) setData(r.data);
      } catch (_e) {
        if (alive) setData(null);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [prospectId]);

  if (!prospectId) return null;

  return (
    <Card data-testid="prospect-email-timeline">
      <CardContent className="p-4 space-y-3">
        <header className="flex items-center gap-2 pb-1 border-b border-slate-100 dark:border-slate-800">
          <Mail className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-bold">Timeline de e-mails</h3>
          {data?.total > 0 && (
            <Badge className="ml-auto bg-slate-100 text-slate-700 text-[10.5px]">
              {data.total} evento(s)
            </Badge>
          )}
        </header>

        {loading && (
          <div className="text-[12px] text-slate-500">Carregando…</div>
        )}

        {!loading && data && !data.email && (
          <div className="text-[12px] text-slate-500"
               data-testid="prospect-email-timeline-empty-noemail">
            {data.message || "Prospect sem e-mail cadastrado."}
          </div>
        )}

        {!loading && data && data.email && data.events.length === 0 && (
          <div className="text-[12.5px] text-slate-500 flex items-center gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 p-2"
               data-testid="prospect-email-timeline-empty">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            Nenhum evento Resend para <span className="font-mono">{data.email}</span>.
          </div>
        )}

        {!loading && data && data.events.length > 0 && (
          <>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(data.counts).map(([kind, n]) => {
                const cls = EVENT_COLOR[kind] || "text-slate-600 border-slate-200 bg-slate-50";
                return (
                  <Badge key={kind} variant="outline"
                         data-testid={`prospect-email-count-${kind}`}
                         className={`text-[10.5px] uppercase tracking-wide ${cls}`}>
                    {kind} · {n}
                  </Badge>
                );
              })}
            </div>
            <ul className="space-y-1.5 max-h-64 overflow-y-auto"
                data-testid="prospect-email-timeline-list">
              {data.events.slice(0, 20).map((ev, i) => {
                const kind = eventKind(ev.type);
                const Icon = EVENT_ICON[kind] || Mail;
                const cls = EVENT_COLOR[kind] || "text-slate-600 border-slate-200 bg-slate-50";
                return (
                  <li key={i}
                      data-testid={`prospect-email-event-${i}`}
                      className={`text-[11.5px] flex items-start gap-2 p-2 rounded border ${cls}`}>
                    <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="font-bold uppercase tracking-wide text-[10px]">
                        {kind}
                        {ev.bounce_type && ` · ${ev.bounce_type}`}
                      </div>
                      {ev.subject && (
                        <div className="truncate text-slate-900 dark:text-white">
                          {ev.subject}
                        </div>
                      )}
                      {ev.click_url && (
                        <a href={ev.click_url} target="_blank" rel="noreferrer noopener"
                           className="truncate underline text-[10.5px] hover:opacity-70">
                          {ev.click_url}
                        </a>
                      )}
                      <div className="text-[10px] opacity-60">{fmt(ev.created_at)}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
